import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, errorMessage } from "../../lib/api";
import { inr } from "../landing/usePricing";

const day = (s) => new Date(s).toLocaleDateString("en-IN");

const JoiningOtp = ({ p, act }) => {
  const [otp, setOtp] = useState("");
  const expired = new Date(p.otp_expires_at) < new Date();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder="4-digit OTP"
        data-testid={`placement-otp-input-${p.id}`} className="h-9 w-32 border border-white/15 bg-[#0B1120] px-3 font-mono tracking-[0.3em] text-white placeholder:tracking-normal" />
      <button disabled={otp.length !== 4} onClick={() => act(`/employer/placements/${p.id}/confirm`, "Joining confirmed. Monthly commission starts now.", { otp })}
        data-testid={`placement-confirm-${p.id}`} className="h-9 bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50">Confirm joining</button>
      <button onClick={() => act(`/employer/placements/${p.id}/new-otp`, "New joining OTP sent to the employee's portal")}
        data-testid={`placement-new-otp-${p.id}`} className={`h-9 border px-3 text-xs ${expired ? "border-orange-500 text-orange-300" : "border-white/15 text-slate-300"}`}>New OTP</button>
    </div>
  );
};

const REASONS = [["left", "Employee left (khud chhoda)"], ["removed", "Removed by us (nikala)"], ["contract_end", "Contract ended (contract khatam)"]];

const ExitBox = ({ p, act }) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [otp, setOtp] = useState("");
  const r = p.exit_request;
  if (r?.status === "pending") return (
    <div className="w-full space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid={`exit-pending-${p.id}`}>
      {r.admin_approved ? (
        <p className="text-xs text-slate-300" data-testid={`exit-approved-${p.id}`}><b className="text-emerald-500">Exit approved by Balangir Group.</b> Your exit OTP: <b className="font-mono text-base tracking-widest text-white">{r.otp}</b>. Enter it below to end the contract.</p>
      ) : (
        <p className="text-xs text-slate-300"><b className="text-amber-500">Exit request sent</b> · {r.reason_label}. Waiting for Balangir Group approval. Monthly commission continues until the exit OTP is entered.</p>
      )}
      <div className="flex flex-wrap gap-2">
        <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder="Exit OTP"
          data-testid={`exit-otp-input-${p.id}`} className="h-9 w-32 border border-white/15 bg-[#0B1120] px-3 font-mono tracking-[0.3em] text-white placeholder:tracking-normal" />
        <button disabled={otp.length !== 4} onClick={() => act(`/employer/placements/${p.id}/exit-confirm`, "Exit approved. Contract cancelled, commission stopped.", { otp })}
          data-testid={`exit-confirm-${p.id}`} className="h-9 bg-red-600 px-3 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">Confirm exit</button>
      </div>
    </div>
  );
  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      {r?.status === "rejected" && <p data-testid={`exit-rejected-${p.id}`} className="w-full text-xs font-semibold text-red-500">Your last exit request was rejected by Balangir Group. The placement stays active and commission continues.</p>}
      <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`exit-reason-${p.id}`} className="h-9 border border-white/15 bg-[#0B1120] px-2 text-xs text-white">
        <option value="">Exit reason…</option>
        {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Note (optional)" data-testid={`exit-note-${p.id}`}
        className="h-9 min-w-0 flex-1 border border-white/15 bg-[#0B1120] px-3 text-xs text-white" />
      <button disabled={!reason} onClick={() => act(`/employer/placements/${p.id}/exit-request`, "Exit request sent to Balangir Group for OTP approval", { reason, note: note.trim() || null })}
        data-testid={`exit-request-${p.id}`} className="h-9 border border-white/15 px-3 text-xs hover:border-red-500 hover:text-red-400 disabled:opacity-50">Request exit</button>
    </div>
  );
};

export const Placements = ({ refresh, onChange }) => {
  const [items, setItems] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    adminApi.get("/employer/placements").then((r) => setItems(r.data)).catch(() => {});
  }, [refresh, tick]);

  const act = (path, msg, body) => adminApi.post(path, body)
    .then(() => { toast.success(msg); setTick((x) => x + 1); onChange?.(); }).catch((e) => toast.error(errorMessage(e)));

  return (
    <section data-testid="placements" className="space-y-3">
      <h2 className="font-heading text-2xl font-bold text-white">Hired employees</h2>
      {items.length === 0 && <p className="text-slate-500">No hired employees yet.</p>}
      {items.map((p) => (
        <div key={p.id} data-testid={`placement-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-[#0F172A] px-5 py-3 text-sm">
          <span><span className="font-semibold text-white">{p.worker_name}</span> · {p.job_role}<br />
            <span data-testid={`placement-status-${p.id}`} className="text-slate-500">
              {p.status === "pending" ? `Joining OTP pending · valid till ${new Date(p.otp_expires_at).toLocaleString("en-IN")}`
                : p.status === "cancelled" ? "Joining cancelled"
                : `Joined ${day(p.started_at)}${p.ended_at ? ` · ended ${day(p.ended_at)}` : ""}`}
            </span></span>
          <span className="font-mono text-slate-300">{inr(p.monthly_rate)} / month</span>
          {p.status === "pending" && <JoiningOtp p={p} act={act} />}
          {p.status === "pending" ? (
            <button onClick={() => act(`/employer/placements/${p.id}/end`, "Joining cancelled")} data-testid={`placement-end-${p.id}`}
              className="border border-white/15 px-3 py-1.5 text-xs hover:border-red-500 hover:text-red-300">Cancel</button>
          ) : p.status === "active" ? <ExitBox p={p} act={act} />
          : <span className="text-xs text-slate-500">{p.status === "cancelled" ? "Cancelled" : `Ended${p.exit_reason ? ` · ${p.exit_reason}` : ""}`}</span>}
        </div>
      ))}
    </section>
  );
};
